import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import EditorCredit from '../components/EditorCredit';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function Billet() {
  const { qr_code } = useParams();
  const [billet, setBillet] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const fetchBillet = () => axios.get(`${API}/billetterie/billet/${qr_code}`).then((r) => setBillet(r.data));

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const sid = sp.get('session_id');
    const paiement = sp.get('paiement');
    fetchBillet().catch((e) => setError(e?.response?.data?.detail || e?.response?.data?.error || 'Billet introuvable'));
    if (paiement === 'success' && sid) {
      setVerifying(true);
      let tries = 0;
      const poll = async () => {
        try {
          const { data } = await axios.get(`${API}/billetterie/paiement/status/${sid}`);
          if (data.payment_status === 'paid' || data.billet_statut === 'valide') {
            setVerifying(false); await fetchBillet(); return;
          }
        } catch { /* transient */ }
        tries += 1;
        if (tries < 10) setTimeout(poll, 2000);
        else { setVerifying(false); fetchBillet(); }
      };
      poll();
    }
    // eslint-disable-next-line
  }, [qr_code]);

  // ── Sharing ──
  const billetUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = billet
    ? `🎫 Mon billet pour "${billet.evt_nom}" (${billet.asso_nom}) — ${new Date(billet.date_debut).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}${billet.siege ? ` · Siège ${billet.siege}` : ''}. Voir le billet :`
    : `🎫 Mon billet AssoPerf`;

  const openShare = (platform) => {
    const u = encodeURIComponent(billetUrl);
    const t = encodeURIComponent(shareText);
    const map = {
      whatsapp: `https://wa.me/?text=${t}%20${u}`,
      telegram: `https://t.me/share/url?url=${u}&text=${t}`,
      sms: `sms:?&body=${t}%20${u}`,
      mail: `mailto:?subject=${encodeURIComponent('Mon billet ' + (billet?.evt_nom || ''))}&body=${t}%20${u}`,
    };
    window.open(map[platform], '_blank', 'noopener');
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: billet?.evt_nom || 'Mon billet', text: shareText, url: billetUrl }); } catch {}
    } else {
      copyLink();
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(billetUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {}
  };

  const containerStyle = {
    minHeight: '100vh',
    background: '#05080a',
    color: '#e2eef8',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
  };

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{ fontSize: '60px', marginBottom: '12px' }}>❌</div>
        <div style={{ fontSize: '20px', fontWeight: 800, marginBottom: '8px' }}>Billet introuvable</div>
        <div style={{ fontSize: '14px', color: '#5a7a8a' }}>{error}</div>
      </div>
    );
  }

  if (!billet) {
    return <div style={containerStyle}><div style={{ fontSize: '14px', color: '#5a7a8a' }}>Chargement…</div></div>;
  }

  const isUsed = billet.statut === 'utilise';
  const isValid = billet.statut === 'valide';
  const brandColor = billet.couleur_principale || '#f5a623';

  return (
    <div style={containerStyle} data-testid="billet-root">
      <div style={{
        background: 'linear-gradient(135deg, #0f1820, #150f20)',
        border: `2px solid ${brandColor}`,
        borderRadius: '20px',
        padding: '28px',
        maxWidth: '420px',
        width: '100%',
        boxShadow: `0 0 40px ${brandColor}33`,
      }}>
        <div style={{ textAlign: 'center', marginBottom: '18px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: brandColor, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>
            {billet.asso_nom}
          </div>
          <div style={{ fontFamily: "'Fraunces', serif", fontSize: '24px', fontWeight: 900, lineHeight: 1.2 }}>
            {billet.evt_nom}
          </div>
        </div>

        <div style={{
          background: 'white',
          padding: '16px',
          borderRadius: '12px',
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '16px',
        }} data-testid="qr-container">
          <img
            src={`${API}/billetterie/qr/${billet.qr_code}.png`}
            alt="QR code"
            style={{ width: '100%', maxWidth: '300px', display: 'block' }}
            data-testid="qr-image"
          />
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          fontSize: '13px',
          padding: '14px',
          background: 'rgba(255,255,255,0.04)',
          borderRadius: '12px',
          marginBottom: '14px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#5a7a8a' }}>Date</span>
            <strong>{new Date(billet.date_debut).toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#5a7a8a' }}>Lieu</span>
            <strong style={{ textAlign: 'right', maxWidth: '60%' }}>{billet.lieu}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#5a7a8a' }}>Zone · Siège</span>
            <strong>{billet.categorie}{billet.siege ? ` · ${billet.siege}` : ''}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#5a7a8a' }}>Tarif</span>
            <strong style={{ color: brandColor }}>{billet.prix_paye}€ · {billet.tarif_type === 'membre' ? 'Membre' : 'Public'}</strong>
          </div>
          {billet.nom_acheteur && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#5a7a8a' }}>Titulaire</span>
              <strong>{billet.nom_acheteur}</strong>
            </div>
          )}
        </div>

        <div style={{
          textAlign: 'center',
          padding: '12px',
          borderRadius: '10px',
          background: isUsed ? 'rgba(255,92,106,0.1)' : (isValid ? 'rgba(34,211,160,0.1)' : 'rgba(255,173,77,0.1)'),
          border: `1px solid ${isUsed ? 'rgba(255,92,106,0.3)' : (isValid ? 'rgba(34,211,160,0.3)' : 'rgba(255,173,77,0.3)')}`,
          fontSize: '13px',
          fontWeight: 700,
          color: isUsed ? '#ff5c6a' : (isValid ? '#22d3a0' : '#ffad4d'),
        }} data-testid="billet-status">
          {verifying ? '⏳ Validation du paiement en cours…' : (isUsed ? '❌ Billet déjà utilisé' : (isValid ? '✅ Billet valide — Présentez-le à l\'entrée' : '⏳ En attente de validation'))}
        </div>

        {/* ── PARTAGE ── */}
        {!isUsed && (
          <div style={{ marginTop: '16px' }} data-testid="share-section">
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#5a7a8a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px', textAlign: 'center' }}>
              Partager mon billet
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              <button
                onClick={() => openShare('whatsapp')}
                style={{ padding: '10px 6px', borderRadius: '10px', background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', color: '#25d366', fontSize: '11px', fontWeight: 700, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', transition: 'transform 0.15s, background 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.background = 'rgba(37,211,102,0.18)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.background = 'rgba(37,211,102,0.1)'; }}
                data-testid="share-whatsapp"
                aria-label="Partager sur WhatsApp"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                <span>WhatsApp</span>
              </button>
              <button
                onClick={() => openShare('telegram')}
                style={{ padding: '10px 6px', borderRadius: '10px', background: 'rgba(34,158,217,0.1)', border: '1px solid rgba(34,158,217,0.3)', color: '#229ed9', fontSize: '11px', fontWeight: 700, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', transition: 'transform 0.15s, background 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.background = 'rgba(34,158,217,0.18)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.background = 'rgba(34,158,217,0.1)'; }}
                data-testid="share-telegram"
                aria-label="Partager sur Telegram"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                <span>Telegram</span>
              </button>
              <button
                onClick={copyLink}
                style={{ padding: '10px 6px', borderRadius: '10px', background: copied ? 'rgba(34,211,160,0.15)' : 'rgba(255,255,255,0.05)', border: `1px solid ${copied ? 'rgba(34,211,160,0.4)' : 'rgba(255,255,255,0.12)'}`, color: copied ? '#22d3a0' : '#e2eef8', fontSize: '11px', fontWeight: 700, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', transition: 'all 0.15s' }}
                onMouseEnter={(e) => { if (!copied) e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                data-testid="share-copy"
                aria-label="Copier le lien"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {copied ? <polyline points="20 6 9 17 4 12"/> : <><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>}
                </svg>
                <span>{copied ? 'Copié !' : 'Copier'}</span>
              </button>
              <button
                onClick={nativeShare}
                style={{ padding: '10px 6px', borderRadius: '10px', background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.3)', color: brandColor, fontSize: '11px', fontWeight: 700, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', transition: 'transform 0.15s, background 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.background = 'rgba(245,166,35,0.18)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.background = 'rgba(245,166,35,0.1)'; }}
                data-testid="share-more"
                aria-label="Plus d'options de partage"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                <span>Plus…</span>
              </button>
            </div>
            <div style={{ marginTop: '8px', textAlign: 'center', fontSize: '10px', color: '#5a7a8a' }}>
              ✨ Invitez vos amis — chaque partage augmente l'audience de l'asso
            </div>

            {/* ── TÉLÉCHARGER LE BILLET (PDF) ── */}
            {(isValid || isUsed) && (
              <a
                href={`${API}/billetterie/billet/${billet.qr_code}/pdf`}
                target="_blank" rel="noreferrer"
                style={{
                  marginTop: '14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  background: 'rgba(34,211,160,0.10)',
                  border: '1px solid rgba(34,211,160,0.35)',
                  color: '#22d3a0',
                  fontSize: '13px', fontWeight: 700,
                  textDecoration: 'none', cursor: 'pointer', transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(34,211,160,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(34,211,160,0.10)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                data-testid="btn-download-pdf"
                aria-label="Télécharger le billet en PDF"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Télécharger le billet (PDF)
              </a>
            )}

            {/* ── AJOUTER À MON AGENDA ── */}
            <a
              href={`${API}/billetterie/ics/${billet.qr_code}`}
              download={`billet-${billet.qr_code}.ics`}
              style={{
                marginTop: '14px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '12px 16px',
                borderRadius: '12px',
                background: 'rgba(79,138,255,0.08)',
                border: '1px solid rgba(79,138,255,0.3)',
                color: '#4f8aff',
                fontSize: '13px', fontWeight: 700,
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(79,138,255,0.16)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(79,138,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              data-testid="btn-add-calendar"
              aria-label="Ajouter à mon agenda"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
                <line x1="12" y1="14" x2="12" y2="18"/>
                <line x1="10" y1="16" x2="14" y2="16"/>
              </svg>
              Ajouter à mon agenda
            </a>
            <div style={{ marginTop: '6px', textAlign: 'center', fontSize: '10px', color: '#5a7a8a' }}>
              Compatible Google Calendar, Apple Calendar, Outlook · Rappel 2h avant
            </div>
          </div>
        )}

        <div style={{ marginTop: '14px', textAlign: 'center', fontSize: '11px', color: '#5a7a8a', fontFamily: 'monospace', letterSpacing: '0.05em' }}>
          {billet.qr_code}
        </div>
      </div>
      <div style={{ marginTop: 24, maxWidth: 800, marginLeft: 'auto', marginRight: 'auto' }}>
        <EditorCredit variant="dark" />
      </div>
    </div>
  );
}
